import { useState, useEffect, useCallback, useRef } from "react";
import { RealtimeChannel } from "@supabase/supabase-js";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Filter, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { TaskCard } from "@/components/dashboard/TaskCard";
import { TaskListSkeleton } from "@/components/dashboard/TaskCardSkeleton";
import { ReferralPopup } from "@/components/dashboard/ReferralPopup";
import { WelcomePopup } from "@/components/dashboard/WelcomePopup";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Task, TaskStatus } from "@/types/task";
import { handleEdgeFunctionError, formatErrorForToast } from "@/lib/errorHandler";
import { useProjects, Project } from "@/hooks/useProjects";
import { ProjectSelector } from "@/components/dashboard/ProjectSelector";

export default function Dashboard() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "all">("all");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [taskToDuplicate, setTaskToDuplicate] = useState<Task | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const { projects, createProject, isLoading: projectsLoading } = useProjects();
  const channelRef = useRef<RealtimeChannel | null>(null);

  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("tasks", {
        method: "GET",
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'task');
        toast(formatErrorForToast(errorInfo));
        return;
      }

      setTasks(data?.tasks ?? []);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      const errorInfo = handleEdgeFunctionError(err, null, 'task');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Supabase Realtime subscription for task updates
  useEffect(() => {
    const setupRealtime = async () => {
      // Get profile ID for filtering
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) return;

      // Subscribe to task changes
      channelRef.current = supabase
        .channel('tasks-realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'tasks',
            filter: `user_id=eq.${profile.id}`
          },
          (payload) => {
            if (payload.eventType === 'INSERT') {
              setTasks(prev => [payload.new as Task, ...prev]);
            } else if (payload.eventType === 'UPDATE') {
              setTasks(prev => prev.map(t => 
                t.id === (payload.new as Task).id ? payload.new as Task : t
              ));
            } else if (payload.eventType === 'DELETE') {
              setTasks(prev => prev.filter(t => t.id !== (payload.old as Task).id));
            }
          }
        )
        .subscribe();
    };

    setupRealtime();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = task.product_name
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || task.status === statusFilter;
    const matchesProject =
      selectedProjectId === null || task.project_id === selectedProjectId;
    return matchesSearch && matchesStatus && matchesProject;
  });

  const handleDeleteClick = (task: Task) => {
    setTaskToDelete(task);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("tasks")
        .delete()
        .eq("id", taskToDelete.id);

      if (error) throw error;

      setTasks((prev) => prev.filter((task) => task.id !== taskToDelete.id));
      toast({
        title: "Task excluída",
        description: "A task foi removida com sucesso.",
      });
    } catch (err) {
      console.error("Error deleting task:", err);
      toast({
        title: "Erro ao excluir",
        description: err instanceof Error ? err.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setTaskToDelete(null);
    }
  };

  const handleDuplicateClick = (task: Task) => {
    setTaskToDuplicate(task);
  };

  const confirmDuplicate = async () => {
    if (!taskToDuplicate) return;
    
    setIsDuplicating(true);
    try {
      const { data, error } = await supabase.functions.invoke("tasks", {
        method: "POST",
        body: {
          product_name: taskToDuplicate.product_name,
          product_description: taskToDuplicate.product_description,
          input_image_url: taskToDuplicate.input_image_url || taskToDuplicate.image_generated_url,
        },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'task');
        
        if (errorInfo.action) {
          toast({
            title: errorInfo.title,
            description: errorInfo.message,
            variant: "destructive",
            action: (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTaskToDuplicate(null);
                  window.location.href = errorInfo.action!.path;
                }}
                className="shrink-0"
              >
                {errorInfo.action.label}
              </Button>
            ),
          });
        } else {
          toast(formatErrorForToast(errorInfo));
        }
        return;
      }

      toast({
        title: "Task duplicada",
        description: "Uma nova task foi criada com base na selecionada.",
      });

      fetchTasks();
    } catch (err) {
      console.error("Error duplicating task:", err);
      const errorInfo = handleEdgeFunctionError(err, null, 'task');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setIsDuplicating(false);
      setTaskToDuplicate(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Minhas Tarefas</h1>
            <p className="text-muted-foreground">
              Gerencie suas tasks de geração de vídeos UGC
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={fetchTasks}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
            <Link to="/dashboard/nova-task">
              <Button variant="hero" className="gap-2">
                <Plus className="w-4 h-4" />
                Nova Task
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <ProjectSelector
            projects={projects}
            selectedProjectId={selectedProjectId}
            onSelectProject={setSelectedProjectId}
            onCreateProject={createProject}
            isLoading={projectsLoading}
          />
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome do produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as TaskStatus | "all")
            }
          >
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="processing">Processando</SelectItem>
              <SelectItem value="finished">Finalizada</SelectItem>
              <SelectItem value="expired">Expirada</SelectItem>
              <SelectItem value="error">Erro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tasks Grid */}
        <div className="space-y-4">
          {isLoading ? (
            <TaskListSkeleton count={3} />
          ) : (
            <AnimatePresence mode="popLayout">
              {filteredTasks.length > 0 ? (
                filteredTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDelete={() => handleDeleteClick(task)}
                    onDuplicate={handleDuplicateClick}
                  />
                ))
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Nenhuma task encontrada
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {search || statusFilter !== "all"
                      ? "Tente ajustar os filtros de busca"
                      : "Comece criando sua primeira task de vídeo UGC"}
                  </p>
                  <Link to="/dashboard/nova-task">
                    <Button variant="hero" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Criar primeira task
                    </Button>
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!taskToDelete} onOpenChange={(open) => !open && setTaskToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Task</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a task "{taskToDelete?.product_name}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Confirmation Dialog */}
      <AlertDialog open={!!taskToDuplicate} onOpenChange={(open) => !open && setTaskToDuplicate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicar Task</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja criar uma nova task com base em "{taskToDuplicate?.product_name}"? 
              Isso irá consumir 1 crédito.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDuplicating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDuplicate} disabled={isDuplicating}>
              {isDuplicating ? "Duplicando..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Welcome Popup */}
      <WelcomePopup />
      {/* Referral Popup */}
      <ReferralPopup />
    </DashboardLayout>
  );
}
